import axios from "axios";

// Runtime-detected API base URL.
// Why: React bakes REACT_APP_BACKEND_URL into the compiled bundle at BUILD time.
// If we hardcoded it, the deployed site (e.g. custom domain like www.medexova.work)
// would call whatever domain was baked in — typically the preview URL — causing CORS
// failures that look like "can't log in" to the user.
// Fix: always call the SAME origin the frontend was served from. Emergent's ingress
// routes /api/* to the backend regardless of which domain the user is on.
// We only fall back to REACT_APP_BACKEND_URL for local dev or tests that need it.
function resolveBackendURL() {
  if (typeof window !== "undefined" && window.location && window.location.origin) {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || "";
}

export const BACKEND_URL = resolveBackendURL();
export const API = `${BACKEND_URL}/api`;

export const apiClient = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("astat_admin_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const RETRYABLE_STATUS = new Set([502, 503, 504, 520, 521, 522, 523, 524]);
const RETRYABLE_METHODS = new Set(["get", "put", "patch", "delete"]);

apiClient.interceptors.response.use(
  (r) => r,
  async (err) => {
    if (err?.response?.status === 401) {
      const path = window.location.pathname;
      // P0 (2026-10-17): this used to only cover /admin/* — but /floor (the
      // Floor dispatcher workspace) is ALSO behind the same ProtectedRoute/
      // /admin/login gate and was never included here. Result: once a
      // session's token expired while a Floor tab stayed open, every single
      // API call 401'd forever with no redirect and no error shown — just a
      // blank white page, since nothing ever told the user to log back in.
      // /floor/preview is intentionally the one Floor route NOT behind auth
      // (a public, no-login mock preview) — excluded so an anonymous visitor
      // there is never bounced to a login page they were never meant to need.
      const isProtectedPath =
        (path.startsWith("/admin") || path.startsWith("/floor")) &&
        path !== "/admin/login" &&
        path !== "/floor/preview";
      if (isProtectedPath) {
        localStorage.removeItem("astat_admin_token");
        window.location.assign("/admin/login");
      }
    }
    // Cloudflare 520/524-style edge errors (and plain network errors) are almost always a
    // request landing in the brief ~1-2s window while the backend hot-reloads during active
    // development — not a real failure. One silent retry for idempotent methods (never POST,
    // to avoid double-submitting a create) turns that into an invisible blip instead of a
    // surfaced "save failed" error.
    const config = err?.config;
    const status = err?.response?.status;
    const isNetworkError = !err?.response;
    const isRetryableStatus = status != null && RETRYABLE_STATUS.has(status);
    const method = (config?.method || "").toLowerCase();
    if (config && !config._retried && RETRYABLE_METHODS.has(method) && (isNetworkError || isRetryableStatus)) {
      config._retried = true;
      await new Promise((res) => setTimeout(res, 800));
      return apiClient(config);
    }
    return Promise.reject(err);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
